import { useState, useEffect, useCallback, useMemo } from 'react';
import debounce from 'lodash/debounce';
import { CHARITY_NUMBER_REGEX } from '@Helpers/fieldValidation';
import { CHARITY_ADDRESS_LOOKUP_DEBOUNCE_MS } from '@Helpers/constants';
import { CharityLookupErrorEnum } from '@Types/CharityData';

export interface CharityLookupResult {
  charityData: any;
  loading: boolean;
  errorType: CharityLookupErrorEnum;
}

// Manages charity number validation and API lookup. Returns validation state for the view layer to handle.
export const useCharityLookup = (
  charityDataApiHook: () => readonly [
    (charityNumber: string) => Promise<any>,
    { readonly data: any; readonly loading: boolean; readonly isError: boolean },
  ],
  charityNumber: string,
): CharityLookupResult => {
  // Call the API hook to get both the function and response state.
  const [getCharityDetails, requestState] = charityDataApiHook();
  const { data: charityResponseData, loading, isError } = requestState;

  // Track the charity number we're currently validating to prevent race conditions with the API call.
  const [validatingCharityNumber, setValidatingCharityNumber] = useState<string>('');

  // Set / clear charity data based on the current state.
  const charityData = useMemo(() => {
    if (
      loading ||
      isError ||
      !charityNumber ||
      charityNumber !== validatingCharityNumber ||
      !CHARITY_NUMBER_REGEX.test(charityNumber)
    ) {
      return null;
    }

    return (charityResponseData as any)?.data;
  }, [charityResponseData, loading, isError, charityNumber, validatingCharityNumber]);

  // Create debounced function for charity lookup.
  const debouncedValidateAndFetch = useCallback(
    debounce((charityNumberToFetch: string) => {
      setValidatingCharityNumber(charityNumberToFetch);

      if (CHARITY_NUMBER_REGEX.test(charityNumberToFetch)) {
        getCharityDetails(charityNumberToFetch);
      }
    }, CHARITY_ADDRESS_LOOKUP_DEBOUNCE_MS),
    [getCharityDetails],
  );

  // Handle charity number changes - immediately clear everything, then start debounce.
  useEffect(() => {
    setValidatingCharityNumber('');

    if (charityNumber) {
      debouncedValidateAndFetch(charityNumber);
    } else {
      // Cancel pending debounced calls if charity number is cleared.
      debouncedValidateAndFetch.cancel();
    }

    // Cleanup function to cancel debounced calls on unmount.
    return () => {
      debouncedValidateAndFetch.cancel();
    };
  }, [charityNumber, debouncedValidateAndFetch]);

  // Determine error type and validity based on current state.
  const errorType: CharityLookupErrorEnum = useMemo(() => {
    // Don't show errors if we're still loading or charity number has changed.
    if (loading || !validatingCharityNumber || charityNumber !== validatingCharityNumber) {
      return CharityLookupErrorEnum.NONE;
    }

    // First check if the charity number matches the expected validation regex.
    const isFormatValid = CHARITY_NUMBER_REGEX.test(validatingCharityNumber);
    if (!isFormatValid) {
      return CharityLookupErrorEnum.INVALID_FORMAT;
    }

    // Check if the API call returned an error.
    if (isError) {
      return CharityLookupErrorEnum.API_ERROR;
    }

    // Check if we actually got data back from the API call and that the number returned in the data matches the charity number we're validating.
    if (!charityData || charityData.charity_number !== validatingCharityNumber) {
      return CharityLookupErrorEnum.NOT_FOUND;
    }

    // If we get here, explicitly return 'none' to indicate no error.
    return CharityLookupErrorEnum.NONE;
  }, [loading, validatingCharityNumber, charityNumber, isError, charityData]);

  return {
    charityData,
    loading,
    errorType,
  };
};
